<template>
  <span v-resize="checkMobile" data-testid="invoices-page">
    <loading :active.sync="isLoading" :is-full-page="true" color="#1976d2"
      :background-color="$vuetify.theme.dark ? '#000' : ''"></loading>
    <v-card>
      <v-toolbar text :color="$vuetify.theme.dark ? '' : 'white'">
        <v-spacer></v-spacer>
        <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('custom.search')" single-line clearable
          hide-details></v-text-field>
        <v-spacer></v-spacer>
        <v-btn small color="success" data-testid="invoice-new-btn" @click="newInvoiceDialog = true">
          {{ $t("invoices.new_invoice") }}
        </v-btn>
        <v-btn icon :href="docsLink('/klienty/scheta')" target="_blank">
          <v-icon>mdi-help-circle</v-icon>
        </v-btn>
      </v-toolbar>
      <v-data-table :headers="headers" :items="invoices" :search="search" :items-per-page="20" :sort-by="['created_at']"
        :loading="isLoading" :loading-text="$t('custom.loading_table')" :sort-desc="[true]" mobile-breakpoint="100"
        :class="!isMobile ? '' : 'is-mobile'" :footer-props="tableFooterProps([20, 50, 100, -1])">
        <template slot="item" slot-scope="props">
          <tr>
            <td class="text-center">{{ props.item.number }}</td>
            <td class="text-center" nowrap>
              <v-icon class="green--text mr-2" @click="paymentStart(props.item)">
                mdi-cash-multiple
              </v-icon>
              <v-icon class="mr-2 mt-1" @click="generateInvoice(props.item)">
                mdi-file-account-outline
              </v-icon>
              <v-icon class="mr-2" @click="editInvoice(props.item.id)">
                mdi-pencil-outline
              </v-icon>
              <v-icon :data-testid="`invoice-delete-${props.item.id}`" @click="deleteItem(props.item)" v-if="checkAdmin()">
                mdi-delete-forever
              </v-icon>
            </td>
            <td class="text-center">
              {{ returnDate(props.item.date) }}
            </td>
            <td class="text-center" :style="user_role == 'guest' || user_role == 'partner'
              ? 'display: none;'
              : ''
              ">
              <router-link :to="{ path: `/clients/${props.item.client_id}` }">
                {{ props.item.client_name }}
              </router-link>
            </td>
            <td class="text-center">
              <router-link :to="{ path: `/bookings/${props.item.booking_id}` }">{{ props.item.booking_id
                }}</router-link>
            </td>
            <td class="text-center">
              <read-more v-if="
                props.item.description && props.item.description.length > 0
              " :more-str="$t('custom.read')" :text="props.item.description" link="#" :less-str="$t('custom.less')"
                :max-chars="20"></read-more>
            </td>
            <td class="text-center">
              {{
                props.item.rent +
                props.item.deposit +
                props.item.delivery +
                props.item.delivery_end +
                props.item.clean +
                props.item.gas +
                props.item.damage +
                props.item.fines +
                props.item.mileage +
                props.item.hours_cost +
                props.item.insurance +
                props.item.add_drivers_cost +
                props.item.equipment +
                (props.item.addons_cost || 0) +
                props.item.other
              }}
            </td>
            <td class="text-center">{{ paidInvoice(props.item) }}</td>
            <td class="text-center">
              {{ props.item.paid ? props.item.paid : 0 }}
            </td>
            <!-- Состояние -->
            <td class="text-center">
              <v-menu absolute offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn :color="stateColor(props.item.state)" small block v-bind="attrs" v-on="on">
                    {{ selectStateLocale(props.item.state) }}
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item link v-for="(state, index) in invoice_state" :key="index" data-testid="invoice-state-option"
                    @click="updateState(props.item, state.value)">
                    <v-list-item-title>
                      {{ state.text }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </td>
          </tr>
        </template>
        <template slot="no-data">
          {{ $t("custom.no_data_in_table") }}
        </template>
        <v-alert slot="no-results" :value="true" color="error" icon="mdi-alert">
          {{ $t("tables.no_search_result", { msg: search }) }}
        </v-alert>
      </v-data-table>
    </v-card>
    <v-dialog v-model="newInvoiceDialog" :retain-focus="false" persistent max-width="1000px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("counts.creating") }}</span>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-md>
            <v-form>
              <v-container>
                <v-layout row wrap class="align-center">
                  <v-flex xs12 md3>
                    <vc-date-picker v-model="date" :attributes="attrs_calendar" :locale="$i18n.locale">
                      <template v-slot="{ inputEvents }">
                        <v-text-field data-testid="invoice-date-field" :value="date_format" v-on="inputEvents" :label="$t('counts.date_initial')"
                          readonly />
                      </template>
                    </vc-date-picker>
                  </v-flex>
                  <v-flex xs12 md3>
                    <v-text-field data-testid="invoice-number-field" v-model="invoice.number" :label="$t('invoices.invoice_number')"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md3>
                    <v-text-field v-model.number="invoice.booking_id"
                      :label="$t('bookings.number_booking')"></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm3 md3>
                    <v-autocomplete data-testid="invoice-client-field" v-model="invoice.client_id" item-text="fullname" item-value="id"
                      :loading="isLoadingClients" :search-input.sync="search_client" clearable flat hide-no-data
                      :label="$t('bookings.select_client')" :items="clients">
                    </v-autocomplete>
                  </v-flex>
                  <v-flex md12 class="d-inline-flex justify-center">
                    <v-text-field data-testid="invoice-rent-field" v-model.number="invoice.rent" :label="$t('bookings.rent')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.deposit" :label="$t('bookings.deposit')" placeholder="0"
                      hide-details></v-text-field>
                  </v-flex>
                  <v-expansion-panels v-model="showAddPaymentsToInvoice">
                    <v-expansion-panel>
                      <v-expansion-panel-header>
                        <h4 class="mt-3 mb-2">
                          {{ $t("custom.add_services") }}
                        </h4>
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <v-flex md12 class="mt-3 d-inline-flex">
                          <v-text-field v-model.number="invoice.delivery" :label="$t('bookings.delivery')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.delivery_end" :label="$t('bookings.checkout')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.clean" :label="$t('bookings.clean')" placeholder="0"
                            class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.gas" :label="$t('bookings.gas')" placeholder="0"
                            class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.hours_cost" :label="$t('bookings.add_hours_short')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.damage" :label="$t('bookings.damages')" placeholder="0"
                            hide-details></v-text-field>
                        </v-flex>
                        <v-flex md12 class="mt-3 d-inline-flex">
                          <v-text-field v-model.number="invoice.insurance" :label="$t('bookings.insurance')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.add_drivers_cost"
                            :label="$t('bookings.add_drivers_small')" placeholder="0" class="mr-2"
                            hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.fines" :label="$t('bookings.fines')" placeholder="0"
                            class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.equipment" :label="$t('bookings.equipment')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.mileage" :label="$t('bookings.many_mileage')"
                            placeholder="0" class="mr-2" hide-details></v-text-field>
                          <v-text-field v-model.number="invoice.other" :label="$t('bookings.other')" placeholder="0"
                            hide-details></v-text-field>
                        </v-flex>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                  <v-flex xs12 sm12 md12>
                    <v-textarea v-model="invoice.description" :label="$t('custom.description')" rows="1" auto-grow
                      :placeholder="$t('custom.add_info')"></v-textarea>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="
            (newInvoiceDialog = false),
            (date = null),
            (invoice = {
              sum: null,
              state: 'Создан',
              date: null,
              description: null,
              number: null,
              client_id: null,
              booking_id: null,
            })
            ">{{ $t("custom.close") }}</v-btn>
          <v-btn data-testid="invoice-save-btn" color="blue darken-1" text @click="submit()">{{
            $t("custom.save")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editDialog" :retain-focus="false" persistent max-width="1000px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("custom.editing") }}</span>
        </v-card-title>

        <v-card-text>
          <v-container grid-list-md>
            <v-form>
              <v-container>
                <v-layout row wrap>
                  <v-flex xs12 md3>
                    <vc-date-picker v-model="date" :attributes="attrs_calendar" :locale="$i18n.locale">
                      <template v-slot="{ inputEvents }">
                        <v-text-field :value="date_format" v-on="inputEvents" :label="$t('counts.date_initial')"
                          readonly />
                      </template>
                    </vc-date-picker>
                  </v-flex>
                  <v-flex xs12 md2>
                    <v-text-field v-model="invoice.number" :label="$t('counts.number')"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md2>
                    <v-text-field v-model.number="invoice.booking_id"
                      :label="$t('bookings.number_booking')"></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm3 md3>
                    <v-autocomplete v-model="invoice.client_id" item-text="fullname" item-value="id"
                      :loading="isLoadingClients" :search-input.sync="search_client" clearable flat hide-no-data
                      :label="$t('bookings.select_client')" :items="clients">
                    </v-autocomplete>
                  </v-flex>
                  <v-flex md12 class="d-inline-flex justify-center">
                    <v-text-field v-model.number="invoice.rent" :label="$t('bookings.rent')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.deposit" :label="$t('bookings.deposit')" placeholder="0"
                      hide-details></v-text-field>
                  </v-flex>
                  <v-flex md12 class="mt-3 d-inline-flex">
                    <v-text-field v-model.number="invoice.delivery" :label="$t('bookings.delivery')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.delivery_end" :label="$t('bookings.checkout')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.clean" :label="$t('bookings.clean')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.gas" :label="$t('bookings.gas')" placeholder="0" class="mr-2"
                      hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.hours_cost" :label="$t('bookings.add_hours_short')"
                      placeholder="0" class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.damage" :label="$t('bookings.damages')" placeholder="0"
                      hide-details></v-text-field>
                  </v-flex>
                  <v-flex md12 class="mt-3 d-inline-flex">
                    <v-text-field v-model.number="invoice.insurance" :label="$t('bookings.insurance')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.add_drivers_cost" :label="$t('bookings.add_drivers_small')"
                      placeholder="0" class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.fines" :label="$t('bookings.fines')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.equipment" :label="$t('bookings.equipment')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.mileage" :label="$t('bookings.many_mileage')" placeholder="0"
                      class="mr-2" hide-details></v-text-field>
                    <v-text-field v-model.number="invoice.other" :label="$t('bookings.other')" placeholder="0"
                      hide-details></v-text-field>
                  </v-flex>
                  <v-flex xs12 sm12 md12>
                    <v-textarea v-model="invoice.description" :label="$t('custom.description')" rows="1" auto-grow
                      :placeholder="$t('custom.add_info')"></v-textarea>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="
            (editDialog = false),
            (date = null),
            (invoice = {
              rent: null,
              deposit: null,
              delivery: null,
              delivery_end: null,
              clean: null,
              gas: null,
              damage: null,
              fines: null,
              mileage: null,
              hours_cost: null,
              insurance: null,
              equipment: null,
              other: null,
              state: 'Создан',
              date: null,
              description: null,
              number: null,
              client_id: null,
              booking_id: null,
            })
            ">{{ $t("custom.close") }}</v-btn>
          <v-btn color="blue darken-1" text @click="saveInvoice()">{{
            $t("custom.save")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="paymentDialog" :retain-focus="false" persistent max-width="800px">
      <v-card>
        <v-card-title>
          <v-row>
            <v-col cols="12" class="d-flex align-center">
              <span class="headline">{{ $t("invoices.pay") }}</span>
              <v-icon class="ml-2 green--text" @click="savePayment()">
                mdi-content-save-all-outline
              </v-icon>
              <v-spacer></v-spacer>
              <v-icon :class="$vuetify.theme.dark ? 'ml-2 white--text' : 'ml-2 black--text'" @click="closePaymentDialog()">
                mdi-close
              </v-icon>
            </v-col>
            <v-col cols="12">
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" class="mt-3">
              <v-row align="center">
                <v-col auto>
                  <v-text-field data-testid="invoice-payment-rent" v-model.number="paymentData.rent.sum" :label="$t('bookings.rent')" :messages="messageInBaseCurrency(paymentData.rent.sum, paymentData.rent.company_cashbox.currency_id)" placeholder="0" dense></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select
                    v-model="paymentData.rent.company_cashbox"
                    :items="active_company_cashboxes"
                    :item-text="item => item.name"
                    :return-object="true"
                    @change="paymentData.rent.currency_id = paymentData.rent.company_cashbox.currency_id, paymentData.rent.company_cashbox_id = paymentData.rent.company_cashbox.id"
                    :label="$t('companies.currency')"
                    dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-row align="center">
                <v-col auto>
                  <v-text-field v-model.number="paymentData.deposit.sum" :label="$t('bookings.deposit')" :messages="messageInBaseCurrency(paymentData.deposit.sum, paymentData.deposit.currency_id)" placeholder="0" dense></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select
                    v-model="paymentData.deposit.company_cashbox"
                    :items="active_company_cashboxes"
                    :item-text="item => item.name"
                    :return-object="true"
                    @change="paymentData.deposit.currency_id = paymentData.deposit.company_cashbox.currency_id, paymentData.deposit.company_cashbox_id = paymentData.deposit.company_cashbox.id"
                    :label="$t('companies.currency')"
                    dense
                  ></v-select>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" >
              <v-expansion-panels v-model="showAddPayments">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <h4 class="mt-3 mb-2">
                      {{ $t("custom.add_services") }}
                    </h4>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.delivery.sum" :label="$t('bookings.delivery')" :messages="messageInBaseCurrency(paymentData.delivery.sum, paymentData.delivery.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.delivery.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.delivery.currency_id = paymentData.delivery.company_cashbox.currency_id, paymentData.delivery.company_cashbox_id = paymentData.delivery.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.delivery_end.sum" :label="$t('bookings.checkout')" :messages="messageInBaseCurrency(paymentData.delivery_end.sum, paymentData.delivery_end.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.delivery_end.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.delivery_end.currency_id = paymentData.delivery_end.company_cashbox.currency_id, paymentData.delivery_end.company_cashbox_id = paymentData.delivery_end.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.clean.sum" :label="$t('bookings.clean')" :messages="messageInBaseCurrency(paymentData.clean.sum, paymentData.clean.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.clean.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.clean.currency_id = paymentData.clean.company_cashbox.currency_id, paymentData.clean.company_cashbox_id = paymentData.clean.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.gas.sum" :label="$t('bookings.gas')" :messages="messageInBaseCurrency(paymentData.gas.sum, paymentData.gas.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.gas.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.gas.currency_id = paymentData.gas.company_cashbox.currency_id, paymentData.gas.company_cashbox_id = paymentData.gas.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.hours_cost.sum" :label="$t('bookings.add_hours_short')" :messages="messageInBaseCurrency(paymentData.hours_cost.sum, paymentData.hours_cost.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.hours_cost.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.hours_cost.currency_id = paymentData.hours_cost.company_cashbox.currency_id, paymentData.hours_cost.company_cashbox_id = paymentData.hours_cost.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.damage.sum" :label="$t('bookings.damages')" :messages="messageInBaseCurrency(paymentData.damage.sum, paymentData.damage.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.damage.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.damage.currency_id = paymentData.damage.company_cashbox.currency_id, paymentData.damage.company_cashbox_id = paymentData.damage.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.insurance.sum" :label="$t('bookings.insurance')" :messages="messageInBaseCurrency(paymentData.insurance.sum, paymentData.insurance.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.insurance.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.insurance.currency_id = paymentData.insurance.company_cashbox.currency_id, paymentData.insurance.company_cashbox_id = paymentData.insurance.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.add_drivers_cost.sum" :label="$t('bookings.add_drivers_small')" :messages="messageInBaseCurrency(paymentData.add_drivers_cost.sum, paymentData.add_drivers_cost.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.add_drivers_cost.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.add_drivers_cost.currency_id = paymentData.add_drivers_cost.company_cashbox.currency_id, paymentData.add_drivers_cost.company_cashbox_id = paymentData.add_drivers_cost.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.fines.sum" :label="$t('bookings.fines')" :messages="messageInBaseCurrency(paymentData.fines.sum, paymentData.fines.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.fines.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.fines.currency_id = paymentData.fines.company_cashbox.currency_id, paymentData.fines.company_cashbox_id = paymentData.fines.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.equipment.sum" :label="$t('bookings.equipment')" :messages="messageInBaseCurrency(paymentData.equipment.sum, paymentData.equipment.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.equipment.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.equipment.currency_id = paymentData.equipment.company_cashbox.currency_id, paymentData.equipment.company_cashbox_id = paymentData.equipment.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.mileage.sum" :label="$t('bookings.many_mileage')" :messages="messageInBaseCurrency(paymentData.mileage.sum, paymentData.mileage.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.mileage.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.mileage.currency_id = paymentData.mileage.company_cashbox.currency_id, paymentData.mileage.company_cashbox_id = paymentData.mileage.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-row align="center">
                          <v-col auto>
                            <v-text-field v-model.number="paymentData.other.sum" :label="$t('bookings.other')" :messages="messageInBaseCurrency(paymentData.other.sum, paymentData.other.currency_id)" dense></v-text-field>
                          </v-col>
                          <v-col auto v-if="company && company.multicurrency_cashboxes">
                            <v-select
                              v-model="paymentData.other.company_cashbox"
                              :items="active_company_cashboxes"
                              :item-text="item => item.name"
                              :return-object="true"
                              @change="paymentData.other.currency_id = paymentData.other.company_cashbox.currency_id, paymentData.other.company_cashbox_id = paymentData.other.company_cashbox.id"
                              :label="$t('companies.currency')"
                              dense
                            ></v-select>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
            <v-col cols="12" class="mt-3 text-center align-center">
              <v-select v-model="paymentData.state" clearable :items="invoice_state" item-text="text" item-value="value" :label="$t('bookings.select_state')"></v-select>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePaymentDialog()">{{ $t("custom.close") }}</v-btn>
          <v-btn data-testid="invoice-payment-save-btn" color="success" @click="savePayment()">{{ $t("custom.save") }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>
<script>
import { clientFullName } from "@/utils/personName";
import moment from "moment";
import { setMomentLocale } from "@/utils/localeInfra";
import "moment/locale/ru";
import "moment/locale/en-gb";
// moment.locale("ru");
import Docxtemplater from "docxtemplater";
import axios from "axios";
// import VueAxios from 'vue-axios'
import PizZip from "pizzip";
// import PizZipUtils from "pizzip/utils/index.js";
import { saveAs } from "file-saver";
import { rubles } from "rubles";
import { resolveDefaultCashbox } from "@/helpers/cashboxes";
// var rubles = require('rubles').rubles;
import { Capacitor } from "@capacitor/core";
import { translateInvoiceState as translateInvoiceStateText } from "@/utils/localeInfra";
import { updatedInvoiceRow, invoiceClientName } from "@/utils/invoiceUpdateResponse";
import { phoneLastDigits } from "@/lib/phone/display";
export default {
  pullToRefresh: true,
  data() {
    return {
      invoices: [],
      invoice: {
        rent: null,
        deposit: null,
        delivery: null,
        delivery_end: null,
        clean: null,
        gas: null,
        damage: null,
        fines: null,
        mileage: null,
        hours_cost: null,
        insurance: null,
        add_drivers_cost: null,
        equipment: null,
        other: null,
        state: "Создан",
        date: null,
        description: null,
        number: null,
        client_id: null,
        booking_id: null,
      },
      invoice_doc_template_url: null,
      invoice_state: [
        { text: this.$t("invoice_state.created"), value: "Создан" },
        { text: this.$t("invoice_state.send"), value: "Выставлен" },
        { text: this.$t("invoice_state.paid_part"), value: "Частично оплачен" },
        { text: this.$t("invoice_state.paid"), value: "Оплачен" },
      ],
      date: null,
      date_format: null,
      date_menu: false,
      date_menu1: false,
      showAddPayments: false,
      showAddPaymentsToInvoice: false,
      headers: [
        { text: this.$t("custom.number"), align: "center", value: "number" },
        { text: this.$t("custom.editor"), sortable: false, align: "center" },
        {
          text: this.$t("counts.date_initial"),
          value: "date",
          align: "center",
        },
        { text: this.$t("custom.client"), value: "client_id", align: "center" },
        {
          text: this.$t("bookings.booking"),
          value: "booking_id",
          align: "center",
        },
        {
          text: this.$t("custom.description"),
          value: "description",
          align: "center",
        },
        { text: this.$t("custom.amount"), value: "sum", align: "center" },
        { text: this.$t("invoices.control"), sortable: false, align: "center" },
        { text: this.$t("invoices.get"), value: "paid", align: "center" },
        { text: this.$t("custom.state"), value: "state", align: "center" },
      ],
      attrs_calendar: [
        {
          key: "today",
          highlight: {
            color: "blue",
            fillMode: "outline",
            contentClass: "italic",
          },
          dates: new Date(),
        },
      ],
      paymentData: {
        rent: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        deposit: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        delivery: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        delivery_end: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        clean: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        mileage: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        hours_cost: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        insurance: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        add_drivers_cost: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        equipment: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        fines: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        gas: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        damage: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        other: {
          sum: null,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        invoice_id: null,
        state: "Оплачен",
      },
      clients: [],
      editClientId: null,
      client: null,
      isLoadingClients: false,
      showClientSearch: false,
      search_client: null,
      search: null,
      paymentDialog: false,
      pagination: { sortBy: "created_at", descending: true },
      editDialog: false,
      newInvoiceDialog: false,
      timeout: null,
      isMobile: false,
      isLoading: true,
      error: "",
    };
  },
  mounted() {
    let self = this;
  },
  created() {
    let self = this;
    // moment Locale
    setMomentLocale(this.$i18n.locale);
    if (this.$store.getters.isLoggedIn) {
      this.isLoading = true;
      let default_cashbox = resolveDefaultCashbox(this.active_company_cashboxes);
      this.paymentData.rent.company_cashbox = default_cashbox;
      this.paymentData.rent.currency_id = default_cashbox.currency_id;
      this.paymentData.rent.company_cashbox_id = default_cashbox.id;
      this.paymentData.deposit.company_cashbox = default_cashbox;
      this.paymentData.deposit.currency_id = default_cashbox.currency_id;
      this.paymentData.deposit.company_cashbox_id = default_cashbox.id;
      this.paymentData.delivery.company_cashbox = default_cashbox;
      this.paymentData.delivery.currency_id = default_cashbox.currency_id;
      this.paymentData.delivery.company_cashbox_id = default_cashbox.id;
      this.paymentData.delivery_end.company_cashbox = default_cashbox;
      this.paymentData.delivery_end.currency_id = default_cashbox.currency_id;
      this.paymentData.delivery_end.company_cashbox_id = default_cashbox.id;
      this.paymentData.clean.company_cashbox = default_cashbox;
      this.paymentData.clean.currency_id = default_cashbox.currency_id;
      this.paymentData.clean.company_cashbox_id = default_cashbox.id;
      this.paymentData.mileage.company_cashbox = default_cashbox;
      this.paymentData.mileage.currency_id = default_cashbox.currency_id;
      this.paymentData.mileage.company_cashbox_id = default_cashbox.id;
      this.paymentData.hours_cost.company_cashbox = default_cashbox;
      this.paymentData.hours_cost.currency_id = default_cashbox.currency_id;
      this.paymentData.hours_cost.company_cashbox_id = default_cashbox.id;
      this.paymentData.insurance.company_cashbox = default_cashbox;
      this.paymentData.insurance.currency_id = default_cashbox.currency_id;
      this.paymentData.insurance.company_cashbox_id = default_cashbox.id;
      this.paymentData.add_drivers_cost.company_cashbox = default_cashbox;
      this.paymentData.add_drivers_cost.currency_id = default_cashbox.currency_id;
      this.paymentData.add_drivers_cost.company_cashbox_id = default_cashbox.id;
      this.paymentData.equipment.company_cashbox = default_cashbox;
      this.paymentData.equipment.currency_id = default_cashbox.currency_id;
      this.paymentData.equipment.company_cashbox_id = default_cashbox.id;
      this.paymentData.fines.company_cashbox = default_cashbox;
      this.paymentData.fines.currency_id = default_cashbox.currency_id;
      this.paymentData.fines.company_cashbox_id = default_cashbox.id;
      this.paymentData.damage.company_cashbox = default_cashbox;
      this.paymentData.damage.currency_id = default_cashbox.currency_id;
      this.paymentData.damage.company_cashbox_id = default_cashbox.id;
      this.paymentData.gas.company_cashbox = default_cashbox;
      this.paymentData.gas.currency_id = default_cashbox.currency_id;
      this.paymentData.gas.company_cashbox_id = default_cashbox.id;
      this.paymentData.other.company_cashbox = default_cashbox;
      this.paymentData.other.currency_id = default_cashbox.currency_id;
      this.paymentData.other.company_cashbox_id = default_cashbox.id;

      this.axios
        .get("/api/v1/all_invoices", {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.invoices = response.data;
          this.axios
            .get(`api/v1/invoice_templates`, {
              headers: {
                Authorization: self.$store.getters.getAuthToken,
              },
            })
            .then((response) => {
              if (response.data) {
                self.invoice_doc_template_url = response.data.url;
              } else {
                if (self.$i18n.locale == "ru") {
                  self.invoice_doc_template_url =
                    "https://web.rentprog.ru/invoice_example.docx";
                } else {
                  self.invoice_doc_template_url =
                    "https://web.rentprog.com/invoice_example_en.docx";
                }
              }
              if (self.$route.params != {} && self.$route.params.query) {
                self.search = self.$route.params.query;
              }
            })
            .catch((error) => {
              self.setError(error, self.$t("errors.invoices_templates_load"));
              console.log(error);
            });
        })
        .catch((error) => {
          this.setError(error, this.$t("errors.invoices_load"));
          console.log(error);
        })
        .finally(() => (this.isLoading = false));
    } else {
      this.$store.commit("unsetCurrentUser");
    }
  },
  watch: {
    search_client(val) {
      if (this.invoice && this.invoice.client_id) {
        return;
      }
      if (this.search_client == "" || this.search_client == " ") {
        this.search_client = null;
      } else {
        let self = this;
        function debounce() {
          if (self.timeout) clearTimeout(self.timeout);

          self.timeout = setTimeout(() => {
            // your action
            self.loadClients();
          }, 200); // delay
        }
        debounce();
      }
    },
    date() {
      if (this.date) {
        this.date_format = moment(this.date, "YYYY-MM-DD").format("DD-MM-YYYY");
      }
    },
  },
  computed: {
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id) {
            cashboxes.push(cashbox);
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    active_company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id && cashbox.is_active) {
            cashboxes.push(cashbox);
          }
        });
        cashboxes.sort((a, b) => {
          // сортировка по is_default
          if (a.is_default && !b.is_default) {
            return -1;
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    base_currency_id() {
      if (this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
        // Если дефолтная касса не помечена — берём первую, не падаем на undefined.
        const def = this.active_company_cashboxes.find((cashbox) => cashbox.is_default) || this.active_company_cashboxes[0];
        return def.currency_id;
      } else {
        return null;
      }
    },
    exchange_rates() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.exchange_rates) {
        return this.$store.getters.getCompany.company.exchange_rates;
      } else {
        return [];
      }
    },
    worker_accounts() {
      if (this.$store.getters.getCurrentUser) {
        return this.$store.getters.getCurrentUser.currency_accounts;
      } else {
        return [];
      }
    },
    currency() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currency
      ) {
        return this.$store.getters.getCompany.company.currency;
      } else {
        return "";
      }
    },
    company() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company) {
        return this.$store.getters.getCompany.company;
      }
      return undefined;
    },
  },
  methods: {
    clientFullName,
    submit() {
      if (this.isStaff) {
        let invoice = this.invoice;
        let self = this;
        let invoice_sum =
          invoice.rent +
          invoice.deposit +
          invoice.delivery +
          invoice.delivery_end +
          invoice.clean +
          invoice.gas +
          invoice.damage +
          invoice.fines +
          invoice.mileage +
          invoice.hours_cost +
          invoice.insurance +
          invoice.equipment +
          (invoice.addons_cost || 0) + // FT-102: каталожное оборудование (у ручной формы поля нет — 0)
          invoice.other;
        if (
          invoice_sum > 0 &&
          invoice.client_id &&
          invoice.number &&
          this.date
        ) {
          this.isLoading = true;
          this.axios
            .post(
              "/api/v1/invoices",
              {
                invoices: {
                  rent: invoice.rent > 0 ? invoice.rent : 0,
                  deposit: invoice.deposit > 0 ? invoice.deposit : 0,
                  delivery: invoice.delivery > 0 ? invoice.delivery : 0,
                  delivery_end:
                    invoice.delivery_end > 0 ? invoice.delivery_end : 0,
                  clean: invoice.clean > 0 ? invoice.clean : 0,
                  gas: invoice.gas > 0 ? invoice.gas : 0,
                  damage: invoice.damage > 0 ? invoice.damage : 0,
                  fines: invoice.fines > 0 ? invoice.fines : 0,
                  mileage: invoice.mileage > 0 ? invoice.mileage : 0,
                  hours_cost: invoice.hours_cost > 0 ? invoice.hours_cost : 0,
                  insurance: invoice.insurance > 0 ? invoice.insurance : 0,
                  add_drivers_cost:
                    invoice.add_drivers_cost > 0 ? invoice.add_drivers_cost : 0,
                  equipment: invoice.equipment > 0 ? invoice.equipment : 0,
                  other: invoice.other > 0 ? invoice.other : 0,
                  date: self.date,
                  state: invoice.state,
                  number: invoice.number,
                  description: invoice.description,
                  client_id: invoice.client_id,
                  booking_id: invoice.booking_id,
                },
              },
              {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              }
            )
            .then((response) => {
              this.invoices.unshift(response.data);

              this.invoice = {
                sum: null,
                rent: null,
                deposit: null,
                delivery: null,
                delivery_end: null,
                clean: null,
                gas: null,
                damage: null,
                fines: null,
                mileage: null,
                hours_cost: null,
                insurance: null,
                equipment: null,
                other: null,
                state: "Создан",
                date: null,
                description: null,
                number: null,
                client_id: null,
                booking_id: null,
              };
              this.newInvoiceDialog = false;

              this.$notify(this.$t("invoices.added"), this.$t("invoices.added_text"), "success");
            })
            .catch((error) => {
              this.setError(error, this.$t("errors.add_invoice"));
            })
            .finally(() => (this.isLoading = false));
        } else {
          this.$notify(this.$t("errors.all_fields_title"), this.$t("invoices.required_fields"), "warning", 4000);
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    editInvoice(id) {
      // Правим КОПИЮ строки: v-model диалога иначе менял строку списка на месте, и
      // отменённая правка («Закрыть») или отказ сервера оставляли в списке несохранённые
      // значения, а повторное открытие брало снимок клиента уже изменённым (ревью vue#541).
      // Строку заменяет только успешное сохранение (splice в saveInvoice).
      this.invoice = { ...this.invoices.find((invoice) => invoice.id == id) };
      // Клиент до правки — по нему видно, сменили ли клиента (подпись строки тогда новая).
      this.editClientId = this.invoice.client_id;
      this.date = moment(this.invoice.date).format("YYYY-MM-DD");
      this.editDialog = true;
    },
    saveInvoice() {
      if (this.isStaff) {
        let invoice = this.invoice;
        let self = this;
        // При правке обязателен только клиент — как на сервере (Invoice belongs_to :client).
        // Сумма, номер и дата у старых счетов бывают пустыми, и прежняя проверка «как при
        // создании» не давала поправить такой счёт вовсе (ревью vue#541). Условие через
        // запятую до того проверяло только дату — счёт без клиента уходил на сервер.
        if (invoice.client_id) {
          this.isLoading = true;
          this.axios
            .patch(
              `/api/v1/invoices/${invoice.id}`,
              {
                invoices: {
                  rent: invoice.rent > 0 ? invoice.rent : 0,
                  deposit: invoice.deposit > 0 ? invoice.deposit : 0,
                  delivery: invoice.delivery > 0 ? invoice.delivery : 0,
                  delivery_end:
                    invoice.delivery_end > 0 ? invoice.delivery_end : 0,
                  clean: invoice.clean > 0 ? invoice.clean : 0,
                  gas: invoice.gas > 0 ? invoice.gas : 0,
                  damage: invoice.damage > 0 ? invoice.damage : 0,
                  fines: invoice.fines > 0 ? invoice.fines : 0,
                  mileage: invoice.mileage > 0 ? invoice.mileage : 0,
                  hours_cost: invoice.hours_cost > 0 ? invoice.hours_cost : 0,
                  insurance: invoice.insurance > 0 ? invoice.insurance : 0,
                  add_drivers_cost:
                    invoice.add_drivers_cost > 0 ? invoice.add_drivers_cost : 0,
                  equipment: invoice.equipment > 0 ? invoice.equipment : 0,
                  other: invoice.other > 0 ? invoice.other : 0,
                  date: self.date,
                  state: invoice.state,
                  number: invoice.number,
                  description: invoice.description,
                  client_id: invoice.client_id,
                  booking_id: invoice.booking_id,
                },
              },
              {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              }
            )
            .then((response) => {
              // 2xx без тела — не успех (см. utils/invoiceUpdateResponse). Клиента сменили —
              // подпись берём у выбранного в списке (иначе строка показала бы прежнего).
              const clientChanged = invoice.client_id !== this.editClientId;
              const selected = clientChanged ? this.clients.find((client) => client.id === invoice.client_id) : null;
              const invoice_data = updatedInvoiceRow(response, invoice, {
                clientName: clientChanged ? invoiceClientName(selected) || invoice.client_id : undefined,
              });
              if (!invoice_data) {
                this.$notify(this.$t("errors.update_invoice"), this.$t("errors.something_went_wrong"), "error", 6000);
                return;
              }
              let foundIndex = this.invoices.findIndex(
                (element) => element.id === invoice.id
              );
              this.invoices.splice(foundIndex, 1, invoice_data);

              this.invoice = {
                rent: null,
                deposit: null,
                delivery: null,
                delivery_end: null,
                clean: null,
                gas: null,
                damage: null,
                fines: null,
                mileage: null,
                hours_cost: null,
                insurance: null,
                add_drivers_cost: null,
                equipment: null,
                other: null,
                state: "Создан",
                date: null,
                description: null,
                number: null,
                client_id: null,
                booking_id: null,
              };
              this.date = null;
              this.editDialog = false;

              this.$notify(this.$t("invoices.updated"), this.$t("invoices.updated_text"), "success");
            })
            .catch((error) => {
              this.setError(error, this.$t("errors.update_invoice"));
            })
            .finally(() => (this.isLoading = false));
        } else {
          this.$notify(this.$t("errors.all_fields_title"), this.$t("bookings.select_client"), "warning", 4000);
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    paymentStart(invoice) {
      this.paymentData = {
        rent: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        deposit: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        delivery: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        delivery_end: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        clean: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        gas: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        damage: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        fines: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        mileage: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        hours_cost: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        insurance: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        add_drivers_cost: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        equipment: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        other: {
          sum: 0,
          company_cashbox: null,
          company_cashbox_id: null,
          currency_id: null,
        },
        invoice_id: invoice.id,
        state: "Оплачен",
      };
      let default_cashbox = resolveDefaultCashbox(this.active_company_cashboxes);
      this.paymentData.rent.company_cashbox = default_cashbox;
      this.paymentData.rent.currency_id = default_cashbox.currency_id;
      this.paymentData.rent.company_cashbox_id = default_cashbox.id;
      this.paymentData.deposit.company_cashbox = default_cashbox;
      this.paymentData.deposit.currency_id = default_cashbox.currency_id;
      this.paymentData.deposit.company_cashbox_id = default_cashbox.id;
      this.paymentData.delivery.company_cashbox = default_cashbox;
      this.paymentData.delivery.currency_id = default_cashbox.currency_id;
      this.paymentData.delivery.company_cashbox_id = default_cashbox.id;
      this.paymentData.delivery_end.company_cashbox = default_cashbox;
      this.paymentData.delivery_end.currency_id = default_cashbox.currency_id;
      this.paymentData.delivery_end.company_cashbox_id = default_cashbox.id;
      this.paymentData.clean.company_cashbox = default_cashbox;
      this.paymentData.clean.currency_id = default_cashbox.currency_id;
      this.paymentData.clean.company_cashbox_id = default_cashbox.id;
      this.paymentData.mileage.company_cashbox = default_cashbox;
      this.paymentData.mileage.currency_id = default_cashbox.currency_id;
      this.paymentData.mileage.company_cashbox_id = default_cashbox.id;
      this.paymentData.hours_cost.company_cashbox = default_cashbox;
      this.paymentData.hours_cost.currency_id = default_cashbox.currency_id;
      this.paymentData.hours_cost.company_cashbox_id = default_cashbox.id;
      this.paymentData.insurance.company_cashbox = default_cashbox;
      this.paymentData.insurance.currency_id = default_cashbox.currency_id;
      this.paymentData.insurance.company_cashbox_id = default_cashbox.id;
      this.paymentData.add_drivers_cost.company_cashbox = default_cashbox;
      this.paymentData.add_drivers_cost.currency_id = default_cashbox.currency_id;
      this.paymentData.add_drivers_cost.company_cashbox_id = default_cashbox.id;
      this.paymentData.equipment.company_cashbox = default_cashbox;
      this.paymentData.equipment.currency_id = default_cashbox.currency_id;
      this.paymentData.equipment.company_cashbox_id = default_cashbox.id;
      this.paymentData.fines.company_cashbox = default_cashbox;
      this.paymentData.fines.currency_id = default_cashbox.currency_id;
      this.paymentData.fines.company_cashbox_id = default_cashbox.id;
      this.paymentData.damage.company_cashbox = default_cashbox;
      this.paymentData.damage.currency_id = default_cashbox.currency_id;
      this.paymentData.damage.company_cashbox_id = default_cashbox.id;
      this.paymentData.gas.company_cashbox = default_cashbox;
      this.paymentData.gas.currency_id = default_cashbox.currency_id;
      this.paymentData.gas.company_cashbox_id = default_cashbox.id;
      this.paymentData.other.company_cashbox = default_cashbox;
      this.paymentData.other.currency_id = default_cashbox.currency_id;
      this.paymentData.other.company_cashbox_id = default_cashbox.id;

      this.paymentDialog = true;
    },
    savePayment() {
      if (this.isStaff) {
        this.isLoading = true;
        let self = this;
        if (
          (this.paymentData.rent.sum ||
            this.paymentData.deposit.sum ||
            this.paymentData.delivery.sum ||
            this.paymentData.delivery_end.sum ||
            this.paymentData.clean.sum ||
            this.paymentData.gas.sum ||
            this.paymentData.damage.sum ||
            this.paymentData.fines.sum ||
            this.paymentData.mileage.sum ||
            this.paymentData.hours_cost.sum ||
            this.paymentData.insurance.sum ||
            this.paymentData.add_drivers_cost.sum ||
            this.paymentData.equipment.sum ||
            this.paymentData.other.sum) &&
          this.paymentData.state &&
          this.paymentData.invoice_id
        ) {

          this.axios
            .post(
              `/api/v1/invoice_payment_v2`,
              {
                payments_data: this.paymentData,
                state: this.paymentData.state,
                invoice_id: this.paymentData.invoice_id,
              },
              {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              }
            )
            .then((response) => {
              let payment_sum = response.data.payment_sum
              let foundIndex = this.invoices.findIndex(
                (element) => element.id === self.paymentData.invoice_id
              );
              this.invoices[foundIndex].state = this.paymentData.state;
              this.invoices[foundIndex].paid += payment_sum;
              this.closePaymentDialog();

              this.$notify(this.$t("invoices.payment_added"), this.$t("invoices.payment_added_text"), "success");
            })
            .catch((error) => {
              this.setError(error, this.$t("errors.paid_invoice"));
            })
            .finally(() => (this.isLoading = false));
        } else {
          this.isLoading = false;
          this.$notify(this.$t("custom.no_data"), "", "warning", 3000);
        }
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    loadClients() {
      this.isLoadingClients = true;
      this.axios
        .post(
          `/api/v1/index_new_booking_page`,
          {
            search: this.search_client,
            meilisearch: true,
          },
          {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          }
        )
        .then((response) => {
          console.log(response.data)
          this.clients = response.data.query ? response.data.query : (response.data ? response.data : []);
          this.clients.forEach((client) => {
            if (!client.entity) {
              client["fullname"] =
                clientFullName(client) + " " +
                (client.phone
                  ? phoneLastDigits(client.phone)
                  : "");
            } else {
              client["fullname"] =
                client.entity_name +
                ", " +
                client.reg_form +
                " " +
                " - " +
                (client.entity_phone
                  ? phoneLastDigits(client.entity_phone)
                  : "");
            }
          });
        })
        .catch((error) => {
          this.isLoadingClients = false;
          this.setError(error, this.$t("errors.clients_load"));
          console.log(error);
        })
        .finally(() => (this.isLoadingClients = false));
    },
    updateState(invoice, state) {
      if (this.isStaff) {
        this.isLoading = true;
        let self = this;
        this.axios
          .patch(
            `/api/v1/invoices/${invoice.id}`,
            {
              invoices: {
                state: state,
              },
            },
            {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            }
          )
          .then((response) => {
            // 2xx без тела — не успех (см. utils/invoiceUpdateResponse).
            const invoice_data = updatedInvoiceRow(response, invoice);
            if (!invoice_data) {
              this.$notify(this.$t("errors.edited_count"), this.$t("errors.something_went_wrong"), "error", 6000);
              return;
            }
            let foundIndex = this.invoices.findIndex(
              (element) => element.id === invoice.id
            );
            this.invoices.splice(foundIndex, 1, invoice_data);

            this.$notify(this.$t("invoices.edited_title"), this.$t("invoices.edited_text"), "success");
          })
          .catch((error) => {
            this.setError(error, this.$t("errors.edited_count"));
          })
          .finally(() => (this.isLoading = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    selectStateLocale(state) {
      return translateInvoiceStateText(this.$t.bind(this), state);
    },
    stateColor(state) {
      if (state == "Выставлен") {
        return "warning";
      }
      if (state == "Оплачен") {
        return "success";
      }
      if (state == "Частично оплачен") {
        return "secondary";
      }
    },
    deleteItem(invoice) {
      if (this.isStaff) {
        this.$confirm({
          title: this.$t("custom.you_sure_title"),
          text: this.$t("custom.you_sure_text"),
          icon: "mdi-alert-circle-outline",
          confirmButtonText: this.$t("custom.you_sure_yes"),
          cancelButtonText: this.$t("custom.you_sure_no"),
        }).then((result) => {
          if (result) {
            this.axios
              .delete(`/api/v1/invoices/${invoice.id}`, {
                headers: {
                  Authorization: this.$store.getters.getAuthToken,
                },
              })
              .then((response) => {
                this.invoices.splice(this.invoices.indexOf(invoice), 1);
              })
              .catch((error) =>
                this.setError(error, this.$t("errors.remove_invoice"))
              )
              .finally(() => (this.isLoading = false));
            this.$notify(this.$t("custom.deleted"), this.$t("invoices.remove_success"), "success");
          }
        });
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    generateInvoice(invoice) {
      this.isLoading = true;
      let self = this;
      let content, error, url;
      url = self.invoice_doc_template_url;
      axios
        .get(url, {
          responseType: "arraybuffer",
          timeout: 120000, // выгрузка шаблона может не влезть в глобальные 30с на медленной сети
          headers: {
            "Content-Type": "text/plain;charset=utf-8",
          },
        })
        .then((response) => {
          content = response.data;
          var zip = new PizZip(content);
          this.isLoading = true;
          this.axios
            .get(`/api/v1/clients/${invoice.client_id}`, {
              headers: {
                Authorization: this.$store.getters.getAuthToken,
              },
            })
            .then((response) => {
              let client = response.data.client;
              let booking = null;

              if (invoice.booking_id) {
                this.axios
                  .get(`/api/v1/bookings/${invoice.booking_id}`, {
                    headers: {
                      Authorization: this.$store.getters.getAuthToken,
                    },
                  })
                  .then((response) => {
                    booking = response.data;

                    var doc = new Docxtemplater().loadZip(zip);
                    doc.setOptions({ nullGetter: () => "" });

                    let invoice_sum =
                      invoice.rent +
                      invoice.deposit +
                      invoice.delivery +
                      invoice.delivery_end +
                      invoice.clean +
                      invoice.gas +
                      invoice.damage +
                      invoice.fines +
                      invoice.mileage +
                      invoice.hours_cost +
                      invoice.insurance +
                      invoice.equipment +
                      (invoice.addons_cost || 0) +
                      invoice.add_drivers_cost +
                      invoice.other;
                    let services = [];
                    // if (invoice.rent > 0) {
                    //   services.push({
                    //     service: "Аренда автомобиля",
                    //     sum: invoice.rent
                    //   });
                    // }
                    if (invoice.deposit > 0) {
                      services.push({
                        service: self.$t("invoices.deposit"),
                        sum: invoice.deposit,
                      });
                    }
                    if (invoice.delivery > 0) {
                      services.push({
                        service: self.$t("invoices.delivery"),
                        sum: invoice.delivery,
                      });
                    }
                    if (invoice.delivery_end > 0) {
                      services.push({
                        service: self.$t("invoices.delivery_end"),
                        sum: invoice.delivery_end,
                      });
                    }
                    if (invoice.clean > 0) {
                      services.push({
                        service: self.$t("invoices.clean"),
                        sum: invoice.clean,
                      });
                    }
                    if (invoice.gas > 0) {
                      services.push({
                        service: self.$t("invoices.fuel"),
                        sum: invoice.gas,
                      });
                    }
                    if (invoice.damage > 0) {
                      services.push({
                        service: self.$t("invoices.damages"),
                        sum: invoice.damage,
                      });
                    }
                    if (invoice.fines > 0) {
                      services.push({
                        service: self.$t("invoices.fines"),
                        sum: invoice.fines,
                      });
                    }
                    if (invoice.mileage > 0) {
                      services.push({
                        service: self.$t("invoices.mileage"),
                        sum: invoice.mileage,
                      });
                    }
                    if (invoice.hours_cost > 0) {
                      services.push({
                        service: self.$t("invoices.add_hours"),
                        sum: invoice.hours_cost,
                      });
                    }
                    if (invoice.insurance > 0) {
                      services.push({
                        service: self.$t("invoices.insurance"),
                        sum: invoice.insurance,
                      });
                    }
                    if (invoice.equipment > 0) {
                      services.push({
                        service: self.$t("invoices.equipment"),
                        sum: invoice.equipment,
                      });
                    }
                    if (invoice.add_drivers_cost > 0) {
                      services.push({
                        service: self.$t("invoices.add_drivers_cost"),
                        sum: invoice.add_drivers_cost,
                      });
                    }
                    if (invoice.other > 0) {
                      services.push({
                        service: self.$t("invoices.other"),
                        sum: invoice.other,
                      });
                    }

                    // Для нумерации в цикле
                    services.forEach((s) => {
                      s.number = services.indexOf(s) + 2;
                    });

                    doc.setData({
                      date: moment().format("DD.MM.YYYY"),
                      date_short: moment().format("DD/MM"),
                      start_date:
                        booking && booking.start_date
                          ? booking.start_date
                          : "      ",
                      end_date:
                        booking && booking.end_date
                          ? booking.end_date
                          : "      ",
                      start_date_short: booking.start_date
                        ? moment(booking.start_date, "DD-MM-YYYY H:mm").format(
                          "DD.MM.YYYY"
                        )
                        : "     ",
                      end_date_short: booking.end_date
                        ? moment(booking.end_date, "DD-MM-YYYY H:mm").format(
                          "DD.MM.YY"
                        )
                        : "     ",
                      day_cost:
                        booking && booking.price ? booking.price : "     ",
                      cost:
                        booking && booking.rental_cost
                          ? booking.rental_cost
                          : "      ",
                      days: booking && booking.days ? booking.days : "      ",
                      car_name:
                        booking && booking.car_name
                          ? booking.car_name
                          : "      ",
                      number: invoice.number,
                      rent: invoice.rent > 0 ? invoice.rent : 0,
                      deposit: invoice.deposit > 0 ? invoice.deposit : 0,
                      delivery: invoice.delivery > 0 ? invoice.delivery : 0,
                      delivery_end:
                        invoice.delivery_end > 0 ? invoice.delivery_end : 0,
                      clean: invoice.clean > 0 ? invoice.clean : 0,
                      gas: invoice.gas > 0 ? invoice.gas : 0,
                      damage: invoice.damage > 0 ? invoice.damage : 0,
                      fines: invoice.fines > 0 ? invoice.fines : 0,
                      mileage: invoice.mileage > 0 ? invoice.mileage : 0,
                      hours_cost:
                        invoice.hours_cost > 0 ? invoice.hours_cost : 0,
                      insurance: invoice.insurance > 0 ? invoice.insurance : 0,
                      add_drivers_cost:
                        invoice.add_drivers_cost > 0
                          ? invoice.add_drivers_cost
                          : 0,
                      equipment: invoice.equipment > 0 ? invoice.equipment : 0,
                      other: invoice.other > 0 ? invoice.other : 0,
                      sum: invoice_sum,
                      sum_words: rubles(invoice_sum),
                      entity_name: client.entity_name,
                      ceo: client.ceo ? client.ceo : "                 ",
                      reg_form: client.reg_form ? client.reg_form : "    ",
                      entity_phone: client.entity_phone
                        ? client.entity_phone
                        : "                ",
                      entity_adress: client.entity_adress
                        ? client.entity_adress
                        : "                              ",
                      entity_address: client.entity_adress
                        ? client.entity_adress
                        : "                              ",
                      city: client.city ? client.city : "      ",
                      country: client.country ? client.country : "      ",
                      inn: client.inn,
                      ogrn: client.ogrn,
                      acc_number: client.acc_number,
                      bank: client.bank,
                      korr: client.korr,
                      bik: client.bik,
                      doc_number: client.doc_number
                        ? client.doc_number
                        : "    ",
                      services: services.length > 0 ? services : null,
                    });
                    doc.render();
                    var out = doc.getZip().generate({
                      type: "blob",
                      mimeType:
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                    }); //Output the document using Data-URI
                    let date = moment().format("Y_MM_DD");
                    // console.log(client)
                    saveAs(out, date + "_" + client.entity_name + ".docx");
                  })
                  .catch((error) => {
                    this.setError(error, this.$t("errors.booking_load"));
                    console.log(error);
                  });
              } else {
                var doc = new Docxtemplater().loadZip(zip);
                doc.setOptions({ nullGetter: () => "" });
                let invoice_sum =
                  invoice.rent +
                  invoice.deposit +
                  invoice.delivery +
                  invoice.delivery_end +
                  invoice.clean +
                  invoice.gas +
                  invoice.damage +
                  invoice.fines +
                  invoice.mileage +
                  invoice.hours_cost +
                  invoice.insurance +
                  invoice.equipment +
                  (invoice.addons_cost || 0) +
                  invoice.other;
                let services = [];
                // if (invoice.rent > 0) {
                //   services.push({
                //     service: "Аренда автомобиля",
                //     sum: invoice.rent
                //   });
                // }
                if (invoice.deposit > 0) {
                  services.push({
                    service: self.$t("invoices.deposit"),
                    sum: invoice.deposit,
                  });
                }
                if (invoice.delivery > 0) {
                  services.push({
                    service: self.$t("invoices.delivery"),
                    sum: invoice.delivery,
                  });
                }
                if (invoice.delivery_end > 0) {
                  services.push({
                    service: self.$t("invoices.delivery_end"),
                    sum: invoice.delivery_end,
                  });
                }
                if (invoice.clean > 0) {
                  services.push({
                    service: self.$t("invoices.clean"),
                    sum: invoice.clean,
                  });
                }
                if (invoice.gas > 0) {
                  services.push({
                    service: self.$t("invoices.fuel"),
                    sum: invoice.gas,
                  });
                }
                if (invoice.damage > 0) {
                  services.push({
                    service: self.$t("invoices.damages"),
                    sum: invoice.damage,
                  });
                }
                if (invoice.fines > 0) {
                  services.push({
                    service: self.$t("invoices.fines"),
                    sum: invoice.fines,
                  });
                }
                if (invoice.mileage > 0) {
                  services.push({
                    service: self.$t("invoices.mileage"),
                    sum: invoice.mileage,
                  });
                }
                if (invoice.hours_cost > 0) {
                  services.push({
                    service: self.$t("invoices.add_hours"),
                    sum: invoice.hours_cost,
                  });
                }
                if (invoice.insurance > 0) {
                  services.push({
                    service: self.$t("invoices.insurance"),
                    sum: invoice.insurance,
                  });
                }
                if (invoice.equipment > 0) {
                  services.push({
                    service: self.$t("invoices.equipment"),
                    sum: invoice.equipment,
                  });
                }
                if (invoice.add_drivers_cost > 0) {
                  services.push({
                    service: self.$t("invoices.add_drivers_cost"),
                    sum: invoice.add_drivers_cost,
                  });
                }
                if (invoice.other > 0) {
                  services.push({
                    service: self.$t("invoices.other"),
                    sum: invoice.other,
                  });
                }
                // Для нумерации в цикле
                services.forEach((s) => {
                  s.number = services.indexOf(s) + 2;
                });
                doc.setData({
                  date: moment().format("DD.MM.YYYY"),
                  date_short: moment().format("DD/MM"),
                  start_date:
                    booking && booking.start_date
                      ? booking.start_date
                      : "      ",
                  end_date:
                    booking && booking.end_date ? booking.end_date : "      ",
                  day_cost: booking && booking.price ? booking.price : "     ",
                  cost:
                    booking && booking.rental_cost
                      ? booking.rental_cost
                      : "      ",
                  days: booking && booking.days ? booking.days : "      ",
                  car_name:
                    booking && booking.car_name ? booking.car_name : "      ",
                  number: invoice.number,
                  rent: invoice.rent > 0 ? invoice.rent : 0,
                  deposit: invoice.deposit > 0 ? invoice.deposit : 0,
                  delivery: invoice.delivery > 0 ? invoice.delivery : 0,
                  delivery_end:
                    invoice.delivery_end > 0 ? invoice.delivery_end : 0,
                  clean: invoice.clean > 0 ? invoice.clean : 0,
                  gas: invoice.gas > 0 ? invoice.gas : 0,
                  damage: invoice.damage > 0 ? invoice.damage : 0,
                  fines: invoice.fines > 0 ? invoice.fines : 0,
                  mileage: invoice.mileage > 0 ? invoice.mileage : 0,
                  hours_cost: invoice.hours_cost > 0 ? invoice.hours_cost : 0,
                  insurance: invoice.insurance > 0 ? invoice.insurance : 0,
                  add_drivers_cost:
                    invoice.add_drivers_cost > 0 ? invoice.add_drivers_cost : 0,
                  equipment: invoice.equipment > 0 ? invoice.equipment : 0,
                  other: invoice.other > 0 ? invoice.other : 0,
                  sum: invoice_sum,
                  sum_words: rubles(invoice_sum),
                  entity_name: client.entity_name,
                  short_entity_name: client.short_entity_name,
                  ceo: client.ceo,
                  reg_form: client.reg_form,
                  entity_phone: client.entity_phone,
                  city: client.city ? client.city : "      ",
                  country: client.country ? client.country : "      ",
                  entity_adress: client.entity_adress,
                  entity_address: client.entity_adress,
                  inn: client.inn,
                  ogrn: client.ogrn,
                  acc_number: client.acc_number,
                  bank: client.bank,
                  korr: client.korr,
                  bik: client.bik,
                  doc_number: client.doc_number ? client.doc_number : "    ",
                  services: services.length > 0 ? services : null,
                });

                doc.render();

                var out = doc.getZip().generate({
                  type: "blob",
                  mimeType:
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                }); //Output the document using Data-URI
                let date = moment().format("Y_MM_DD");
                // console.log(client)
                saveAs(out, date + "_" + client.entity_name + ".docx");
              }
            })
            .catch((error) => {
              this.setError(error, this.$t("errors.client_load"));
              console.log(error);
            });
        })
        .catch(function (error) {
          console.log(error);
        })
        .finally(() => (this.isLoading = false));
    },
    paidInvoice(invoice) {
      let invoice_sum =
        invoice.rent +
        invoice.deposit +
        invoice.delivery +
        invoice.delivery_end +
        invoice.clean +
        invoice.gas +
        invoice.damage +
        invoice.fines +
        invoice.mileage +
        invoice.hours_cost +
        invoice.insurance +
        invoice.add_drivers_cost +
        invoice.equipment +
        (invoice.addons_cost || 0) +
        invoice.other;
      if (invoice_sum == invoice.paid && invoice_sum > 0 && invoice.paid > 0) {
        return this.$t("invoices.paid");
      } else if (invoice_sum < invoice.paid) {
        return this.$t("invoices.extra_paid");
      } else if (invoice_sum > 0 && invoice_sum > invoice.paid) {
        return this.$t("invoices.low_paid");
      } else if (invoice_sum > 0 && invoice.paid == 0) {
        return this.$t("invoices.no_income");
      } else {
        return `-`;
      }
    },
    closePaymentDialog() {
      let default_cashbox = resolveDefaultCashbox(this.active_company_cashboxes);
      this.paymentData = {
        rent: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        deposit: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        delivery: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        delivery_end: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        clean: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        mileage: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        hours_cost: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        insurance: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        add_drivers_cost: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        equipment: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        fines: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        damage: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        gas: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        other: { sum: 0, company_cashbox: default_cashbox, company_cashbox_id: default_cashbox.id, currency_id: default_cashbox.currency_id },
        invoice_id: null,
        state: "Оплачен",
      };
      this.paymentDialog = false;
    },
    sumInBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) / parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    sumInNonBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) * parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    messageInBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(this.base_currency_id)}`;
      } else {
        return '';
      }
    },
    messageInNonBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInNonBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(currency_id)}`;
      } else {
        return '';
      }
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    formatCreatedAt(date) {
      return moment.parseZone(date).format("DD MMM H:mm");
    },
    returnDate(item) {
      return moment(item, "YYYY-MM-DD").format("LL");
    },
    checkAdmin() {
      return this.$store.getters.isSuperAdmin || this.$store.getters.isAdmin;
    },
    checkMobile() {
      if (this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm") {
        this.isMobile = true;
        return true;
      } else {
        this.isMobile = false;
        return false;
      }
    },
  },
};
</script>
